import { supabase } from '@/integrations/supabase/client';
import type {
  Club,
  ClubVerificationRequest,
  Invitation,
  StaffEntry,
  StaffRole,
} from '@/store/clubs';

/** Data-access layer for clubs, staff, invitations and verification requests. */

export const CLUBS_PAGE_SIZE = 20;

type ClubRow = {
  id: string;
  owner_id: string;
  name: string;
  initials: string;
  bio: string | null;
  location: string | null;
  avatar_url: string | null;
  verified: boolean;
  founded_year: number | null;
  website: string | null;
  instagram: string | null;
  facebook: string | null;
  created_at: string;
};

export function initialsFor(name: string) {
  return name
    .trim()
    .split(/\s+/)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('')
    .slice(0, 2);
}

export function mapClub(row: ClubRow): Club {
  return {
    id: row.id,
    ownerId: row.owner_id,
    name: row.name,
    initials: row.initials || initialsFor(row.name),
    bio: row.bio ?? undefined,
    location: row.location ?? undefined,
    avatarUrl: row.avatar_url ?? undefined,
    verified: row.verified,
    foundedYear: row.founded_year ?? undefined,
    website: row.website ?? undefined,
    instagram: row.instagram ?? undefined,
    facebook: row.facebook ?? undefined,
    createdAt: row.created_at,
  };
}

export async function fetchClubsPage(page: number, pageSize = CLUBS_PAGE_SIZE) {
  const from = page * pageSize;
  const { data, error } = await supabase
    .from('clubs')
    .select('*')
    .order('created_at', { ascending: false })
    .range(from, from + pageSize);
  if (error) throw error;
  const rows = (data ?? []) as unknown as ClubRow[];
  return { items: rows.slice(0, pageSize).map(mapClub), hasMore: rows.length > pageSize };
}

export async function fetchClubsByIds(ids: string[]): Promise<Club[]> {
  if (ids.length === 0) return [];
  const { data, error } = await supabase.from('clubs').select('*').in('id', ids);
  if (error) throw error;
  return ((data ?? []) as unknown as ClubRow[]).map(mapClub);
}

export async function fetchClubById(id: string): Promise<Club | null> {
  const { data, error } = await supabase.from('clubs').select('*').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? mapClub(data as unknown as ClubRow) : null;
}

export async function fetchClubByOwner(ownerId: string): Promise<Club | null> {
  const { data, error } = await supabase
    .from('clubs')
    .select('*')
    .eq('owner_id', ownerId)
    .order('created_at', { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data ? mapClub(data as unknown as ClubRow) : null;
}

export async function insertClub(
  ownerId: string,
  data: { name: string; bio?: string; location?: string; avatarUrl?: string },
): Promise<Club> {
  const { data: row, error } = await supabase
    .from('clubs')
    .insert({
      owner_id: ownerId,
      name: data.name.trim(),
      initials: initialsFor(data.name),
      bio: data.bio?.trim() || null,
      location: data.location?.trim() || null,
      avatar_url: data.avatarUrl || null,
    })
    .select('*')
    .single();
  if (error) throw error;
  return mapClub(row as unknown as ClubRow);
}

export async function patchClub(
  clubId: string,
  data: { name?: string; bio?: string; location?: string; avatarUrl?: string | null },
): Promise<void> {
  const patch: Record<string, unknown> = {};
  if (data.name !== undefined) {
    patch.name = data.name.trim();
    patch.initials = initialsFor(data.name);
  }
  if (data.bio !== undefined) patch.bio = data.bio.trim() || null;
  if (data.location !== undefined) patch.location = data.location.trim() || null;
  if (data.avatarUrl !== undefined) patch.avatar_url = data.avatarUrl;
  if (Object.keys(patch).length === 0) return;
  const { error } = await supabase.from('clubs').update(patch as never).eq('id', clubId);
  if (error) throw error;
}

export async function removeClubRow(clubId: string): Promise<void> {
  const { error } = await supabase.from('clubs').delete().eq('id', clubId);
  if (error) throw error;
}

// -------------------------------------------------------------------- staff

type StaffRow = {
  id: string;
  club_id: string;
  team_id: string | null;
  user_id: string;
  role: string;
  created_at: string;
};

const mapStaff = (row: StaffRow): StaffEntry => ({
  id: row.id,
  clubId: row.club_id,
  teamId: row.team_id ?? undefined,
  userId: row.user_id,
  role: row.role as StaffRole,
  createdAt: row.created_at,
});

export async function fetchStaffForClubs(clubIds: string[]): Promise<StaffEntry[]> {
  if (clubIds.length === 0) return [];
  const { data, error } = await supabase.from('staff_members').select('*').in('club_id', clubIds);
  if (error) throw error;
  return ((data ?? []) as unknown as StaffRow[]).map(mapStaff);
}

export async function insertStaff(data: {
  clubId: string;
  userId: string;
  role: StaffRole;
  teamId?: string;
}): Promise<StaffEntry | null> {
  const { data: row, error } = await supabase
    .from('staff_members')
    .insert({
      club_id: data.clubId,
      user_id: data.userId,
      role: data.role,
      team_id: data.teamId ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) {
    if (error.code === '23505') return null;
    throw error;
  }
  return row ? mapStaff(row as unknown as StaffRow) : null;
}

export async function patchStaff(
  staffId: string,
  patch: { role?: StaffRole; teamId?: string | null },
) {
  const body: Record<string, unknown> = {};
  if (patch.role !== undefined) body.role = patch.role;
  if (patch.teamId !== undefined) body.team_id = patch.teamId;
  if (Object.keys(body).length === 0) return;
  const { error } = await supabase.from('staff_members').update(body as never).eq('id', staffId);
  if (error) throw error;
}

export async function removeStaffRow(staffId: string) {
  const { error } = await supabase.from('staff_members').delete().eq('id', staffId);
  if (error) throw error;
}

// -------------------------------------------------------------- invitations

type InvitationRow = {
  id: string;
  club_id: string;
  team_id: string | null;
  target_user_id: string;
  type: string;
  staff_role: string | null;
  status: string;
  created_at: string;
};

const mapInvitation = (row: InvitationRow): Invitation => ({
  id: row.id,
  clubId: row.club_id,
  teamId: row.team_id ?? undefined,
  targetUserId: row.target_user_id,
  type: row.type as Invitation['type'],
  staffRole: (row.staff_role ?? undefined) as StaffRole | undefined,
  status: row.status as Invitation['status'],
  createdAt: row.created_at,
});

export async function fetchInvitationsForUser(userId: string): Promise<Invitation[]> {
  const { data, error } = await supabase
    .from('club_invitations')
    .select('*')
    .eq('target_user_id', userId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return ((data ?? []) as unknown as InvitationRow[]).map(mapInvitation);
}

export async function fetchInvitationsForClubs(clubIds: string[]): Promise<Invitation[]> {
  if (clubIds.length === 0) return [];
  const { data, error } = await supabase.from('club_invitations').select('*').in('club_id', clubIds);
  if (error) throw error;
  return ((data ?? []) as unknown as InvitationRow[]).map(mapInvitation);
}

export async function insertInvitation(data: {
  clubId: string;
  teamId?: string;
  targetUserId: string;
  invitedBy: string;
  type: 'player' | 'staff';
  staffRole?: StaffRole;
}): Promise<Invitation | null> {
  const { data: row, error } = await supabase
    .from('club_invitations')
    .insert({
      club_id: data.clubId,
      team_id: data.teamId ?? null,
      target_user_id: data.targetUserId,
      invited_by: data.invitedBy,
      type: data.type,
      staff_role: data.staffRole ?? null,
    })
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return row ? mapInvitation(row as unknown as InvitationRow) : null;
}

export async function setInvitationStatus(invitationId: string, status: 'accepted' | 'rejected') {
  const { error } = await supabase
    .from('club_invitations')
    .update({ status })
    .eq('id', invitationId);
  if (error) throw error;
}

// ------------------------------------------------------- verification flows

type VerificationRow = {
  id: string;
  club_id: string;
  official_name: string;
  official_email: string;
  official_website: string | null;
  proof_reference: string;
  notes: string | null;
  status: string;
  created_at: string;
};

const mapVerification = (row: VerificationRow): ClubVerificationRequest => ({
  id: row.id,
  clubId: row.club_id,
  officialName: row.official_name,
  officialEmail: row.official_email,
  officialWebsite: row.official_website ?? '',
  proofReference: row.proof_reference,
  notes: row.notes ?? undefined,
  status: row.status as ClubVerificationRequest['status'],
  createdAt: row.created_at,
});

export async function fetchVerificationRequests(): Promise<ClubVerificationRequest[]> {
  const { data, error } = await supabase
    .from('club_verification_requests')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw error;
  return ((data ?? []) as unknown as VerificationRow[]).map(mapVerification);
}

export async function insertVerificationRequest(data: {
  clubId: string;
  officialName: string;
  officialEmail: string;
  officialWebsite: string;
  proofReference: string;
  notes?: string;
}): Promise<ClubVerificationRequest | null> {
  const { data: row, error } = await supabase
    .from('club_verification_requests')
    .insert({
      club_id: data.clubId,
      official_name: data.officialName,
      official_email: data.officialEmail,
      official_website: data.officialWebsite || null,
      proof_reference: data.proofReference,
      notes: data.notes?.trim() || null,
    })
    .select('*')
    .maybeSingle();
  if (error) {
    if (error.code === '23505') return null;
    throw error;
  }
  return row ? mapVerification(row as unknown as VerificationRow) : null;
}

/** Admin-only: approve or reject a request. The verified badge is admin-gated in SQL. */
export async function reviewVerification(requestId: string, approve: boolean, clubId?: string) {
  const { error } = await supabase
    .from('club_verification_requests')
    .update({ status: approve ? 'approved' : 'rejected' })
    .eq('id', requestId);
  if (error) throw error;
  if (approve && clubId) {
    const { error: clubError } = await supabase
      .from('clubs')
      .update({ verified: true })
      .eq('id', clubId);
    if (clubError) throw clubError;
  }
}

// ------------------------------------------------------------------ cleanup

/** Remove every club trace of a user (owned clubs cascade to teams/rosters). */
export async function purgeUserClubData(userId: string) {
  await supabase.from('team_members').delete().eq('player_id', userId);
  await supabase.from('staff_members').delete().eq('user_id', userId);
  await supabase.from('club_members').delete().eq('user_id', userId);
  await supabase.from('club_invitations').delete().eq('target_user_id', userId);
  await supabase.from('clubs').delete().eq('owner_id', userId);
}
